/**
 * Routes: her saved, ordered lists of spots ("Bloem Noord", "Welkom run"). She places a route
 * on a day by hand; the planner then plans that day in this order. Pure helpers: every edit
 * returns a new route.
 */
import type { Catalog } from "./catalog";

export interface RouteDef {
  readonly id: string;
  readonly name: string;
  /** Spots in driving order. */
  readonly slotIds: readonly string[];
}

/** @throws if a route names a spot the catalog doesn't know, or lists a spot twice. */
export function validateRoute(route: RouteDef, catalog: Catalog): void {
  if (!route.name.trim()) throw new Error("A route needs a name");
  const seen = new Set<string>();
  for (const slotId of route.slotIds) {
    catalog.slot(slotId); // throws for unknown spots
    if (seen.has(slotId)) throw new Error(`"${route.name}" lists a spot twice`);
    seen.add(slotId);
  }
}

export function addSpot(route: RouteDef, slotId: string): RouteDef {
  return route.slotIds.includes(slotId) ? route : { ...route, slotIds: [...route.slotIds, slotId] };
}

export function removeSpot(route: RouteDef, slotId: string): RouteDef {
  return { ...route, slotIds: route.slotIds.filter((s) => s !== slotId) };
}

/** Move the spot at `from` to `to` (both indexes in the current order); out-of-range is a no-op. */
export function moveSpot(route: RouteDef, from: number, to: number): RouteDef {
  const ids = [...route.slotIds];
  if (from < 0 || from >= ids.length || to < 0 || to >= ids.length || from === to) return route;
  const [moved] = ids.splice(from, 1);
  ids.splice(to, 0, moved!);
  return { ...route, slotIds: ids };
}
